import create from "zustand";
import { invoiceService } from "../services";
import { IInvoice, IPaginationMeta } from "../@types";
import { debounce } from "lodash";
import { Filter, IRecurringInvoiceFilter } from "../@types/invoice";

interface FetchOptions {
  startDate?: string;
  endDate?: string;
  actionType?: "initial" | "filter";
}

interface InvoiceStore {
  invoices: IInvoice[];
  hasData: boolean;
  isLoading: boolean;
  isLoadingInitial: boolean;
  isLoadingFilter: boolean;
  isLoadingMore: boolean;
  error: any;
  fetchRecurringInvoices: () => Promise<void>;
  fetchRecurringInvoicesHistory: (reoccurringEntityId: string) => Promise<void>; // Specify entityId type
  fetchData: (options?: FetchOptions) => Promise<void> | undefined;
  fetchRecurringData: (
    reoccurringEntityId: string,
    startDate?: string,
    endDate?: string,
  ) => Promise<void> | undefined;
  fetchMoreData: () => Promise<void>;
  fetchMoreDataHistory: () => Promise<void>;
  setSearchText: (text: string) => void;
  handleSort: (text: string) => void;
  setFilter: (filter: IRecurringInvoiceFilter | Filter) => void;
  removeInvoice: (id: string) => void;
  addInvoice: (invoice: any) => void;
  pagination: IPaginationMeta;
  searchText: string;
  range: string;
  filter: IRecurringInvoiceFilter | Filter;
  reoccurringEntityId: string | null;
  setReoccurringEntityId: (id: string) => void;
  getInvoiceById: (id: string) => Promise<IInvoice | null>;
  clearAllFilters: () => void;
}

export const useInvoiceReoccurringStore = create<InvoiceStore>((set, get) => ({
  invoices: [],
  hasData: false,
  isLoading: false,
  isLoadingInitial: true,
  isLoadingFilter: false,
  isLoadingMore: false,
  error: null,
  searchText: "",
  range: "",
  filter: {
    reoccurringStatus: "",
  },
  pagination: {
    cursor: null,
    backCursor: null,
    hasMoreData: false,
    limit: undefined,
    count: undefined,
  },
  fetchData: debounce(async (options?: FetchOptions) => {
    set({ isLoading: true });
    const { searchText, filter, range } = get();
    const { actionType = "initial" } = options || {};
    if (actionType === "initial") {
      set({ isLoadingInitial: true });
    } else if (actionType === "filter") {
      set({ isLoadingFilter: true });
    }
    const hasFilters =
      range.length > 0 ||
      searchText.length > 0 ||
      ("reoccurringStatus" in filter && filter.reoccurringStatus.length > 0) ||
      ("isReoccurring" in filter && filter.isReoccurring !== null);
    try {
      let query = "";
      if (searchText.length > 0) {
        query += `/invoices/search?q=${searchText}`;
      } else {
        query += `/invoices/reoccurring`;
        if (options?.startDate && options?.endDate) {
          query += `?startDate=${options?.startDate}&endDate=${options?.endDate}`;
        }
        if (hasFilters) {
          query += `?`;
          if (searchText.length > 0) query += `search=${searchText}&`;
          if (range.length > 0) query += `range=${range}&`;
          if (
            "reoccurringStatus" in filter &&
            filter.reoccurringStatus.length > 0
          )
            query += `reoccurringStatus=${filter.reoccurringStatus.toLowerCase()}&`;
          if ("isReoccurring" in filter && filter.isReoccurring !== null)
            query += `isReoccurring=${filter.isReoccurring}&`;
        }
      }
      const res = await invoiceService.get(query);
      let invoices = res.data?.data || res.data;

      if (searchText.length == 0 && !hasFilters && invoices.length > 0) {
        set({ hasData: true });
      }

      if (searchText.length > 0) {
        invoices = res.data;
      }
      if (
        "reoccurringStatus" in filter &&
        filter.reoccurringStatus.length > 0 &&
        searchText.length > 0
      ) {
        invoices = invoices.filter(
          (i: any) =>
            i.reoccurringStatus.toLowerCase() ==
            filter.reoccurringStatus.toLowerCase(),
        );
      }
      set({
        invoices,
        pagination: res.data.meta,
        isLoading: false,
        isLoadingInitial: false,
        isLoadingFilter: false,
      });
    } catch (e: any) {
      set({
        isLoading: false,
        isLoadingInitial: false,
        isLoadingFilter: false,
        error: e.message,
      });
    }
  }, 300),

  fetchRecurringData: debounce(
    async (
      reoccurringEntityId: string,
      startDate?: string,
      endDate?: string,
    ) => {
      set({ isLoading: true });
      const { searchText, range } = get();

      try {
        let query = `/invoices/reoccurring-history?entityId=${reoccurringEntityId}`;
        const queryParams: string[] = [];
        if (startDate && endDate) {
          queryParams.push(`startDate=${encodeURIComponent(startDate)}`);
          queryParams.push(`endDate=${encodeURIComponent(endDate)}`);
        }
        if (range.length > 0) {
          queryParams.push(`range=${encodeURIComponent(range)}`);
        }
        if (searchText.length > 0) {
          queryParams.push(`search=${encodeURIComponent(searchText)}`);
        }
        if (queryParams.length > 0) {
          query += `&${queryParams.join("&")}`;
        }

        const res = await invoiceService.get(query);
        set({
          invoices: res.data.data,
          pagination: res.data.meta,
          isLoading: false,
        });
      } catch (e: any) {
        set({ isLoading: false, error: e.message });
      }
    },
    300,
  ),

  fetchRecurringInvoices: async () => {
    set({ isLoading: true });
    try {
      const res = await invoiceService.get(`/invoices/reoccurring`);
      set({
        invoices: res.data.data,
        pagination: res.data.meta,
        isLoading: false,
      });
    } catch (e: any) {
      set({ isLoading: false, error: e.message });
    }
  },

  fetchRecurringInvoicesHistory: async (reoccurringEntityId: string) => {
    set({ isLoading: true });
    try {
      const res = await invoiceService.get(
        `/invoices/reoccurring-history?entityId=${reoccurringEntityId}`,
      );

      set({
        invoices: res.data.data,
        pagination: res.data.meta,
        isLoading: false,
      });
    } catch (e: any) {
      console.error("Error fetching invoices:", e);
      console.error("Response data:", e.response?.data);
      set({ isLoading: false, error: e.message || "An error occurred" });
    }
  },

  fetchMoreData: async () => {
    const { filter } = get();
    if (!get()?.pagination?.hasMoreData || get()?.isLoading) return;
    set({ isLoading: true });
    const cursor = get().pagination.cursor;
    try {
      let query = `/invoices/reoccurring?cursor=${cursor}`;

      if (
        "reoccurringStatus" in filter &&
        filter.reoccurringStatus.length > 0
      ) {
        query += `&reoccurringStatus=${filter.reoccurringStatus.toLowerCase()}`;
      }

      const res = await invoiceService.get(query);

      set((state) => ({
        invoices: [...state.invoices, ...res.data.data],
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      set({ isLoading: false, error: e.message });
    }
  },
  reoccurringEntityId: null,

  setReoccurringEntityId: (id: string) => {
    set({ reoccurringEntityId: id });
  },

  fetchMoreDataHistory: async () => {
    const { pagination, reoccurringEntityId } = get();
    if (!pagination.hasMoreData || get().isLoading) return;

    set({ isLoading: true });

    if (!reoccurringEntityId) {
      set({ isLoading: false, error: "Entity ID is missing" });
      return;
    }

    const cursor = pagination.cursor;
    try {
      const res = await invoiceService.get(
        `/invoices/reoccurring-history?entityId=${reoccurringEntityId}&cursor=${cursor}`,
      );

      set((state) => ({
        invoices: [...state.invoices, ...res.data.data],
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      set({ isLoading: false, error: e.message });
    }
  },

  setSearchText: (text: string) => {
    set({ searchText: text });
  },
  handleSort: (text: string) => {
    set({ range: text });
  },
  setFilter: (filter: IRecurringInvoiceFilter | Filter) => {
    set({ filter: filter as IRecurringInvoiceFilter });
  },
  removeInvoice: (id) => {
    const invoices = get().invoices;
    set({ invoices: invoices.filter((invoice) => invoice.id !== id) });
  },
  addInvoice: (invoice) => {
    set((state) => ({ invoices: [invoice, ...state.invoices] }));
  },
  getInvoiceById: async (id: string) => {
    set({ isLoading: true });
    try {
      const res = await invoiceService.get(`/invoices/reoccurring/${id}`);
      const invoice = res.data.data;
      set({ isLoading: false });
      return invoice;
    } catch (e: any) {
      console.error(e.response.data);
      set({ isLoading: false, error: e.message });
      return null;
    }
  },
  clearAllFilters: () => {
    set({
      filter: {
        reoccurringStatus: "",
      },
    });
  },
}));
